<template>
  <div class="setup-step">
    <h3>{{ s__('ProjectSetup|Step 2: Language & Timezone') }}</h3>
    <p class="step-description">
      {{
        s__(
          'ProjectSetup|Configure language and timezone for group chat notifications and task deadlines.'
        )
      }}
    </p>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Language') }}</label>
      <select v-model="preferredLanguage" :disabled="saving || loadingTimezones" class="select">
        <option value="en">English</option>
        <option value="ru">Русский</option>
      </select>
      <div class="help-text">
        {{ s__('ProjectSetup|Language for group chat notifications') }}
      </div>
    </div>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Timezone') }}</label>
      <div class="info-banner">
        <div class="info-banner-icon">⏰</div>
        <div class="info-banner-content">
          <div class="info-banner-text">
            {{
              s__(
                'ProjectSetup|The selected timezone will be used to display task deadlines and times in group chat notifications. All task times will be converted to this timezone for team members.'
              )
            }}
          </div>
        </div>
      </div>
      <select v-model="preferredTimezone" :disabled="saving || loadingTimezones" class="select">
        <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
          {{ tz.label }}
        </option>
      </select>
      <div class="help-text">
        {{ s__('ProjectSetup|Timezone for displaying task deadlines in group notifications') }}
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">
      {{ s__('ProjectSetup|Preferences saved successfully!') }}
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, loadTranslations } from '@/locale'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  timezones: {
    type: Array,
    default: () => [],
  },
  detectedTimezone: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
  loadingTimezones: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save', 'skip'])

const preferredLanguage = ref(null)
const preferredTimezone = ref(null)
const originalPreferredLanguage = ref(null)
const originalPreferredTimezone = ref(null)
const saveError = ref('')
const saveSuccess = ref(false)

const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $preferredLanguage: String, $preferredTimezone: String) {
    updateProject(
      id: $id
      preferredLanguage: $preferredLanguage
      preferredTimezone: $preferredTimezone
    ) {
      project {
        id
        preferredLanguage
        preferredTimezone
      }
      errors
    }
  }
`

const { mutate: updateProject } = useMutation(UPDATE_PROJECT_MUTATION)

// Initialize from project
watch(
  () => [props.project, props.timezones],
  ([projectData, timezonesList]) => {
    if (projectData) {
      preferredLanguage.value = projectData.preferredLanguage || 'en' // Default to 'en' if null

      // If project has no timezone set (new project), default to Moscow timezone
      if (!projectData.preferredTimezone && timezonesList && timezonesList.length > 0) {
        const moscowTimezone = timezonesList.find(tz => tz.value === 'Europe/Moscow')
        if (moscowTimezone) {
          preferredTimezone.value = 'Europe/Moscow'
        } else {
          // Fallback to first timezone if Moscow is not available
          preferredTimezone.value = timezonesList[0].value
        }
      } else {
        preferredTimezone.value = projectData.preferredTimezone
      }

      originalPreferredLanguage.value = preferredLanguage.value
      originalPreferredTimezone.value = preferredTimezone.value
    }
  },
  { immediate: true }
)

// Note: We don't watch project's preferredLanguage to change UI locale
// The UI should remain in the user's preferred language (set from user profile)
// The project's preferredLanguage is only for group chat notifications

// Check if there are unsaved changes
function hasChanges() {
  return (
    preferredLanguage.value !== originalPreferredLanguage.value ||
    preferredTimezone.value !== originalPreferredTimezone.value
  )
}

// Reset changes
function reset() {
  preferredLanguage.value = originalPreferredLanguage.value
  preferredTimezone.value = originalPreferredTimezone.value
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  reset,
  async save() {
    if (!props.project?.id) return

    // Ensure timezone is set (should be Moscow for new projects, but double-check)
    if (!preferredTimezone.value && props.timezones && props.timezones.length > 0) {
      const moscowTimezone = props.timezones.find(tz => tz.value === 'Europe/Moscow')
      if (moscowTimezone) {
        preferredTimezone.value = 'Europe/Moscow'
      } else {
        preferredTimezone.value = props.timezones[0].value
      }
    }

    console.log('[SetupLanguageTimezone] save() called', {
      preferredLanguage: preferredLanguage.value,
      preferredTimezone: preferredTimezone.value,
    })

    saveError.value = ''
    saveSuccess.value = false

    try {
      const { data } = await updateProject({
        id: props.project.id,
        preferredLanguage: preferredLanguage.value,
        preferredTimezone: preferredTimezone.value,
      })

      if (data?.updateProject?.errors?.length > 0) {
        throw new Error(data.updateProject.errors.join(', '))
      }

      saveSuccess.value = true
      originalPreferredLanguage.value = preferredLanguage.value
      originalPreferredTimezone.value = preferredTimezone.value
      // Return saved data instead of emitting event - parent will handle step advancement
      return {
        preferredLanguage: preferredLanguage.value,
        preferredTimezone: preferredTimezone.value,
      }
    } catch (e) {
      console.error('[SetupLanguageTimezone] save() error', e)
      saveError.value = e.message || 'Failed to save preferences'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.select {
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.info-banner {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
}

.info-banner-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.info-banner-content {
  flex: 1;
}

.info-banner-text {
  font-size: 13px;
  color: #004085;
  line-height: 1.5;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}
</style>
